import React, { useEffect, useMemo, useState } from "react";
import { PageHeader, DataTable, StatusBadge } from "@/components/common";
import { api, formatApiErrorDetail, BASE, API, getToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Code2, Copy, Check, FileDown, Pencil } from "lucide-react";
import { useAuth } from "@/lib/auth";

const empty = { municipality_id: "", ad_size_id: "", name: "", code_type: "js", status: "active" };

export default function PlacementsPage() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [rows, setRows] = useState([]);
  const [muns, setMuns] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null); // null = create, string = edit
  const [codeOpen, setCodeOpen] = useState(false);
  const [codeData, setCodeData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [codeTab, setCodeTab] = useState("js");
  const [sfSite, setSfSite] = useState("");
  const [filterMun, setFilterMun] = useState("all");
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const [p, m, sz] = await Promise.all([
        api.get("/admin/placements"),
        api.get("/admin/municipalities"),
        api.get("/admin/ad-sizes"),
      ]);
      setRows(p.data); setMuns(m.data); setSizes(sz.data);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const filtered = filterMun === "all" ? rows : rows.filter((r) => r.municipality_id === filterMun);

  const grouped = useMemo(() => {
    const g = {};
    for (const r of rows) {
      if (!g[r.municipality_id]) g[r.municipality_id] = { name: r.municipality_name, ids: [], count: 0 };
      g[r.municipality_id].count++;
    }
    return g;
  }, [rows]);

  function openNew() {
    setEditingId(null);
    setForm({ ...empty, municipality_id: muns[0]?.id || "" });
    setOpen(true);
  }
  function openEdit(r) {
    setEditingId(r.id);
    setForm({
      municipality_id: r.municipality_id,
      ad_size_id: r.ad_size_id,
      name: r.name || "",
      code_type: r.code_type || "js",
      status: r.status || "active",
    });
    setOpen(true);
  }
  async function save() {
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/admin/placements/${editingId}`, form);
        toast.success("Reklam alanı güncellendi");
      } else {
        await api.post("/admin/placements", form);
        toast.success("Reklam alanı eklendi");
      }
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  }
  async function openCode(r) {
    try {
      const { data } = await api.get(`/admin/placements/${r.id}/embed-code`);
      const fixJs = (s) => s.replace(/https?:\/\/[^"]+\/api\/tag\.js/, `${BASE}/api/tag.js`);
      const fixFrame = (s) => s.replace(/https?:\/\/[^"]+(\/api\/frame\/)/, `${BASE}$1`);
      setCodeData({
        ...data,
        js: fixJs(data.js),
        iframe: fixFrame(data.iframe),
        js_safeframe: fixJs(data.js_safeframe || ""),
        iframe_safeframe: fixFrame(data.iframe_safeframe || ""),
      });
      setSfSite(data.sites?.[0]?.domain || "");
      setCopied(false); setCodeTab(r.code_type || "js");
      setCodeOpen(true);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  }
  const safeFrameCode = useMemo(() => {
    if (!codeData?.js_safeframe) return "";
    return codeData.js_safeframe.replace("__SITE_DOMAIN__", sfSite || "SITENIZIN-DOMAINI.com");
  }, [codeData, sfSite]);

  async function copyCode() {
    if (!codeData) return;
    const txt = codeTab === "js" ? codeData.js : codeTab === "iframe" ? codeData.iframe : safeFrameCode;
    try { await navigator.clipboard.writeText(txt); }
    catch {
      const t = document.createElement("textarea"); t.value = txt;
      document.body.appendChild(t); t.select(); document.execCommand("copy"); document.body.removeChild(t);
    }
    setCopied(true); toast.success("Kod panoya kopyalandı");
    setTimeout(() => setCopied(false), 1500);
  }

  async function downloadBulkPdf(munId, munName) {
    try {
      const url = `${API}/admin/placements/bulk-pdf/${munId}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) throw new Error(res.statusText);
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = `turkads-${munName.replace(/\s+/g, "-")}-reklam-kodlari.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(objUrl), 1000);
      toast.success(`${munName} reklam kodları PDF olarak indirildi`);
    } catch (e) { toast.error(`İndirme hatası: ${e.message}`); }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        overline="Kod Üretimi"
        title="Reklam Alanları"
        description={"Her belediye için ölçü başına tek reklam alanı kodu üretilir. Aynı kod, sistemde beyaz listeye eklenmiş tüm yayıncı sitelerinde çalışır."}
        actions={isSuper && (
          <Button className="bg-red-600 hover:bg-red-700 rounded-sm h-10 font-heading font-semibold uppercase text-xs tracking-wider" onClick={openNew} data-testid="add-placement-button">
            <Plus className="w-4 h-4 mr-1.5" /> Yeni Reklam Alanı
          </Button>
        )}
      />

      <div className="bg-white border border-slate-200 rounded-md p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500">Toplu Kod Paketi</div>
            <div className="font-heading font-semibold text-slate-950">Belediye Bazlı PDF İndir</div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {Object.entries(grouped).map(([mid, info]) => (
            <Button
              key={mid}
              onClick={() => downloadBulkPdf(mid, info.name)}
              variant="outline"
              className="rounded-sm h-11 justify-between px-4"
              data-testid={`bulk-pdf-${mid}`}
            >
              <span className="text-left">
                <div className="text-sm font-semibold">{info.name}</div>
                <div className="text-[11px] text-slate-500">{info.count} reklam alanı</div>
              </span>
              <FileDown className="w-4 h-4 text-red-600" />
            </Button>
          ))}
        </div>
      </div>

      {isSuper && (
        <div className="flex items-center gap-3">
          <Label className="text-xs text-slate-600">Belediye Filtresi</Label>
          <Select value={filterMun} onValueChange={setFilterMun}>
            <SelectTrigger className="w-64 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tümü</SelectItem>
              {muns.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      {loading ? <div className="text-sm text-slate-500">Yükleniyor…</div> : (
        <DataTable
          testid="placements-table"
          columns={[
            { label: "Reklam Alanı", render: (r) => <div><div className="font-medium">{r.name}</div><div className="text-xs text-slate-500">{r.municipality_name}</div></div> },
            { label: "Boyut", render: (r) => <span className="font-mono text-xs">{r.ad_size?.name || ""}</span> },
            { label: "Tip", render: (r) => <span className="uppercase text-xs text-slate-500">{r.code_type}</span> },
            { label: "Durum", render: (r) => <StatusBadge status={r.status} /> },
            {
              label: "İşlemler", render: (r) => (
                <div className="flex items-center gap-1.5">
                  <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={() => openCode(r)} data-testid={`code-btn-${r.id}`}>
                    <Code2 className="w-3.5 h-3.5 mr-1.5" /> Embed Kodu
                  </Button>
                  {isSuper && (
                    <Button size="sm" variant="ghost" className="rounded-sm h-8 text-slate-700 hover:bg-slate-100" onClick={() => openEdit(r)} data-testid={`edit-placement-${r.id}`}>
                      <Pencil className="w-3.5 h-3.5 mr-1.5" /> Düzenle
                    </Button>
                  )}
                </div>
              )
            },
          ]}
          rows={filtered}
        />
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-md">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingId ? "Reklam Alanını Düzenle" : "Yeni Reklam Alanı"}</DialogTitle>
            <DialogDescription>Belediye × ölçü başına tek embed kod üretilir. Belediye ve ölçü seçimi oluşturmadan sonra kilitlenir.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Belediye</Label>
              <Select value={form.municipality_id} onValueChange={(v) => setForm({ ...form, municipality_id: v })} disabled={!!editingId}>
                <SelectTrigger data-testid="placement-form-mun"><SelectValue placeholder="Belediye" /></SelectTrigger>
                <SelectContent>{muns.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
              </Select>
              {editingId && <div className="text-[10px] text-slate-500 mt-1">Belediye değişikliği kod bütünlüğünü bozacağı için düzenlenemez.</div>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Reklam Ölçüsü</Label>
                <Select value={form.ad_size_id} onValueChange={(v) => setForm({ ...form, ad_size_id: v })} disabled={!!editingId}>
                  <SelectTrigger data-testid="placement-form-size"><SelectValue placeholder="Boyut" /></SelectTrigger>
                  <SelectContent>{sizes.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Kod Tipi</Label>
                <Select value={form.code_type} onValueChange={(v) => setForm({ ...form, code_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="js">JS Tag</SelectItem><SelectItem value="iframe">iFrame</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Alan Adı (panelde tanıma için)</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Örn: Manşet Üstü Banner" data-testid="placement-form-name" />
            </div>
            {editingId && (
              <div>
                <Label>Durum</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="placement-form-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Aktif</SelectItem>
                    <SelectItem value="paused">Duraklatıldı</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded p-2">
              Bu reklam alanı için üretilecek kod, seçilen belediyeye bağlı <b>tüm beyaz-liste yayıncı sitelerinde</b> çalışacaktır. Listeye eklenmemiş domain&apos;lerde reklam görünmez.
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>İptal</Button>
            <Button onClick={save} disabled={saving} className="bg-red-600 hover:bg-red-700 rounded-sm" data-testid="placement-form-save">
              {saving ? "Kaydediliyor…" : (editingId ? "Güncelle" : "Oluştur")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={codeOpen} onOpenChange={setCodeOpen}>
        <DialogContent className="max-w-2xl rounded-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Embed Kodu</DialogTitle>
            <DialogDescription>Bu kodu yayıncı sitenin ilgili alanına yapıştırın. Kod aynı kalır, panelden değiştirdiğiniz görsel tüm sitelerde otomatik güncellenir.</DialogDescription>
          </DialogHeader>
          {codeData && (
            <div className="space-y-4">
              <div className="text-sm text-slate-600">
                Bu kodu belediye ile anlaşmalı yayıncı sitesinin ilgili alanına (Gutenberg Custom HTML,
                Elementor HTML widget, tema alanı vb.) yapıştırın. Reklam otomatik olarak görünür.
              </div>

              <Tabs value={codeTab} onValueChange={setCodeTab}>
                <TabsList data-testid="code-tabs">
                  <TabsTrigger value="js" data-testid="code-tab-js">JS Tag (Önerilen)</TabsTrigger>
                  <TabsTrigger value="iframe" data-testid="code-tab-iframe">iFrame</TabsTrigger>
                  <TabsTrigger value="safeframe" data-testid="code-tab-safeframe">Google Ad Manager</TabsTrigger>
                </TabsList>
                <TabsContent value="js">
                  <div className="embed-code-box relative">
                    <Button size="sm" variant="secondary" onClick={copyCode}
                      className="absolute top-2 right-2 bg-slate-800 hover:bg-slate-700 text-white text-[11px] uppercase tracking-wider rounded-sm h-7"
                      data-testid="copy-embed-code-btn">
                      {copied ? <><Check className="w-3.5 h-3.5 mr-1" /> Kopyalandı</> : <><Copy className="w-3.5 h-3.5 mr-1" /> Panoya Kopyala</>}
                    </Button>
                    <pre className="whitespace-pre-wrap pr-24"><code>{codeData.js}</code></pre>
                  </div>
                </TabsContent>
                <TabsContent value="iframe">
                  <div className="embed-code-box relative">
                    <Button size="sm" variant="secondary" onClick={copyCode}
                      className="absolute top-2 right-2 bg-slate-800 hover:bg-slate-700 text-white text-[11px] uppercase tracking-wider rounded-sm h-7"
                      data-testid="copy-embed-iframe-btn">
                      {copied ? <><Check className="w-3.5 h-3.5 mr-1" /> Kopyalandı</> : <><Copy className="w-3.5 h-3.5 mr-1" /> Panoya Kopyala</>}
                    </Button>
                    <pre className="whitespace-pre-wrap pr-24"><code>{codeData.iframe}</code></pre>
                  </div>
                </TabsContent>
                <TabsContent value="safeframe">
                  <div className="space-y-3">
                    <div className="text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded p-2">
                      Google Ad Manager reklamları <b>SafeFrame</b> içinde izole edilmiş iframe&apos;den yüklenir; tarayıcı üst
                      sayfanın adresini gizler. Bu kod, sitenin kimliğini <code className="font-mono">data-site</code> ile
                      taşır — beyaz liste kontrolü aynen uygulanır, sadece domain tespiti güvenceye alınır.
                    </div>
                    <div>
                      <Label className="text-xs">Yayıncı Sitesi (bu kodun kullanılacağı site)</Label>
                      <Select value={sfSite} onValueChange={setSfSite}>
                        <SelectTrigger className="h-9" data-testid="safeframe-site-select"><SelectValue placeholder="Site seçin" /></SelectTrigger>
                        <SelectContent>
                          {(codeData.sites || []).map((s) => (
                            <SelectItem key={s.domain} value={s.domain}>{s.name} — {s.domain}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="embed-code-box relative">
                      <Button size="sm" variant="secondary" onClick={copyCode}
                        className="absolute top-2 right-2 bg-slate-800 hover:bg-slate-700 text-white text-[11px] uppercase tracking-wider rounded-sm h-7"
                        data-testid="copy-embed-safeframe-btn">
                        {copied ? <><Check className="w-3.5 h-3.5 mr-1" /> Kopyalandı</> : <><Copy className="w-3.5 h-3.5 mr-1" /> Panoya Kopyala</>}
                      </Button>
                      <pre className="whitespace-pre-wrap pr-24"><code>{safeFrameCode}</code></pre>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>

              <div className="text-xs text-slate-500 grid grid-cols-2 gap-2">
                <div><b>Boyut:</b> {codeData.width}×{codeData.height} px</div>
                <div><b>Placement ID:</b> <span className="font-mono">{codeData.placement.id.slice(0, 12)}…</span></div>
              </div>
              <div className="text-[11px] text-slate-500 bg-amber-50 border border-amber-200 rounded p-2">
                🛡 Bu kod yalnızca beyaz listedeki yayıncı sitelerinde çalışır. Yeni bir site eklemek için <b>Yayıncı Siteleri</b> ekranından belediyeye tanımlayın.
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
